import { api } from '@/config/api';

export interface TiktokAdAccount {
  _id: string;
  accountUid: string;
  companyUid: string;
  advertiserId: string;
  advertiserName?: string;
  currency?: string;
  timezone?: string;
  role?: string;
  appId: string;
  apiBaseUrl: string;
  apiVersion: string;
  isDefault: boolean;
  isActive: boolean;
  lastSyncedAt?: string;
  lastSyncError?: string;
  tokenGrantedAt?: string;
  healthCheckedAt?: string;
  healthVerifiedAt?: string;
  healthError?: string;
  healthNeedsReconnect?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TiktokCampaign {
  _id: string;
  campaignUid: string;
  companyUid: string;
  accountUid: string;
  advertiserId: string;
  tiktokCampaignId: string;
  campaignName: string;
  objectiveType?: string;
  budgetMode?: string;
  budget?: number;
  operationStatus?: string;
  secondaryStatus?: string;
  campaignType?: string;
  createTime?: string;
  modifyTime?: string;
  lastSyncedAt: string;
}

export interface TiktokAdGroup {
  _id: string;
  adGroupUid: string;
  companyUid: string;
  accountUid: string;
  advertiserId: string;
  tiktokAdGroupId: string;
  tiktokCampaignId: string;
  adGroupName: string;
  placementType?: string;
  placements?: string[];
  optimizationGoal?: string;
  bidPrice?: number;
  bidType?: string;
  budget?: number;
  budgetMode?: string;
  scheduleType?: string;
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  operationStatus?: string;
  secondaryStatus?: string;
  createTime?: string;
  modifyTime?: string;
  lastSyncedAt: string;
}

export interface TiktokAd {
  _id: string;
  adUid: string;
  companyUid: string;
  accountUid: string;
  advertiserId: string;
  tiktokAdId: string;
  tiktokAdGroupId: string;
  tiktokCampaignId: string;
  adName: string;
  adFormat?: string;
  adText?: string;
  callToAction?: string;
  landingPageUrl?: string;
  videoId?: string;
  imageIds?: string[];
  identityId?: string;
  operationStatus?: string;
  secondaryStatus?: string;
  createTime?: string;
  modifyTime?: string;
  lastSyncedAt: string;
}

export interface TiktokInsightsRow {
  _id: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversion: number;
  reach: number;
  videoPlayActions: number;
}

export interface TiktokDailyRow {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversion: number;
  reach: number;
  videoPlayActions: number;
  ctr: number;
  cpc: number;
  cpm: number;
}

export interface TiktokAudience {
  audienceId: string;
  name: string;
  size?: number;
  audienceType?: string;
  status?: string;
}

export type TiktokCrmEventType =
  | 'deal_won' | 'deal_created' | 'lead_qualified' | 'lead_created'
  | 'contact_created' | 'task_completed' | 'manual';

export interface TiktokEventRule {
  _id?: string;
  ruleUid: string;
  companyUid: string;
  name: string;
  crmEventType: TiktokCrmEventType;
  pixelCode: string;
  eventName: string;
  valueField?: string;
  currency?: string;
  isEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TiktokEnhanceAdCopyPayload {
  adFormat?: 'SINGLE_VIDEO' | 'SINGLE_IMAGE' | 'CAROUSEL_ADS' | 'COLLECTION_ADS';
  objective?: string;
  draftAdName?: string;
  draftAdText?: string;
  draftCallToAction?: string;
  brandName?: string;
  productOrService?: string;
  landingPageUrl?: string;
  language?: string;
  provider?: 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';
  model?: string;
}

export interface TiktokEnhancedAdCopy {
  adName: string;
  adText: string;
  callToAction: string;
}

export interface TiktokEventRuleInput {
  name: string;
  crmEventType: TiktokCrmEventType;
  pixelCode: string;
  eventName: string;
  valueField?: string;
  currency?: string;
  isEnabled?: boolean;
}

/** One event already configured on a TikTok pixel — an `optimization_event` candidate. */
export interface TiktokPixelEventOption {
  eventType: string;
  label?: string;
}

export interface TiktokPixelOption {
  pixelId: string;
  pixelCode: string;
  pixelName: string;
  events: TiktokPixelEventOption[];
}

/** One video in the advertiser's TikTok creative library. */
export interface TiktokLibraryVideo {
  videoId: string;
  fileName?: string;
  videoCoverUrl?: string;
  previewUrl?: string;
  width?: number;
  height?: number;
  /** Seconds. */
  duration?: number;
  /** Transcoding finished — usable on an ad. */
  displayable?: boolean;
  createTime?: string;
}

/** A creative already used by one of the CRM's synced TikTok ads. */
export interface TiktokAdCreativeRef {
  tiktokAdId: string;
  adName: string;
  adFormat?: string;
  adText?: string;
  callToAction?: string;
  landingPageUrl?: string;
  videoId?: string;
  imageIds?: string[];
  createTime?: string;
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

/**
 * Pixel rows arrive in whatever shape the API layer is currently emitting —
 * camelCase from the mapped backend, snake_case straight off TikTok's
 * /pixel/list/. Read both, drop anything unusable, and dedupe events.
 */
function normalizePixelOptions(raw: unknown): TiktokPixelOption[] {
  if (!Array.isArray(raw)) return [];
  const out: TiktokPixelOption[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const p = row as Record<string, unknown>;
    const pixelId = str(p.pixelId) ?? str(p.pixel_id) ?? str(p.id);
    if (!pixelId) continue;
    const pixelCode = str(p.pixelCode) ?? str(p.pixel_code) ?? str(p.code) ?? pixelId;
    const pixelName = str(p.pixelName) ?? str(p.pixel_name) ?? str(p.name) ?? pixelCode;

    const rawEvents = Array.isArray(p.events)
      ? p.events
      : Array.isArray(p.pixelEvents) ? p.pixelEvents
      : Array.isArray(p.pixel_events) ? p.pixel_events
      : [];

    const seen = new Set<string>();
    const events: TiktokPixelEventOption[] = [];
    for (const e of rawEvents) {
      if (!e || typeof e !== 'object') {
        // Some builds return a bare list of event-type strings.
        const bare = str(e);
        if (bare && !seen.has(bare)) { seen.add(bare); events.push({ eventType: bare }); }
        continue;
      }
      const ev = e as Record<string, unknown>;
      const eventType = str(ev.eventType) ?? str(ev.event_type)
        ?? str(ev.optimizationEvent) ?? str(ev.optimization_event) ?? str(ev.code);
      if (!eventType || seen.has(eventType)) continue;
      seen.add(eventType);
      events.push({ eventType, label: str(ev.label) ?? str(ev.name) ?? str(ev.eventName) });
    }

    out.push({ pixelId, pixelCode, pixelName, events });
  }
  return out;
}

const BASE = '/slighter_crm/tiktok';

export interface TiktokOauthStatus {
  productionConfigured: boolean;
}

export const tiktokService = {
  oauthStart: async (): Promise<{ url: string }> => (await api.get(`${BASE}/oauth/start`, { withCredentials: true })).data,
  oauthStatus: async (): Promise<TiktokOauthStatus> => (await api.get(`${BASE}/oauth/status`)).data,
  listAccounts: async (): Promise<TiktokAdAccount[]> => (await api.get(`${BASE}/accounts`)).data,
  getDefault: async (): Promise<TiktokAdAccount | null> => (await api.get(`${BASE}/accounts/default`)).data,
  setDefault: async (accountUid: string): Promise<TiktokAdAccount> =>
    (await api.post(`${BASE}/accounts/${accountUid}/set-default`)).data,
  setActive: async (accountUid: string, isActive: boolean): Promise<TiktokAdAccount> =>
    (await api.patch(`${BASE}/accounts/${accountUid}/active`, { isActive })).data,
  remove: async (accountUid: string): Promise<{ ok: true }> =>
    (await api.delete(`${BASE}/accounts/${accountUid}`)).data,
  refreshInfo: async (accountUid: string): Promise<{
    currency: string | null;
    advertiserName: string | null;
    timezone: string | null;
    changed: boolean;
    verified: boolean;
    checkedAt: string;
    error?: string;
    needsReconnect?: boolean;
  }> => (await api.post(`${BASE}/accounts/${accountUid}/refresh-info`)).data,

  /** Spendable balance: ad-account level + Business Center portfolio cash. */
  getAccountBalance: async (accountUid: string): Promise<{
    currency: string | null;
    accountBalance: number | null;
    bcCashBalance: number | null;
    grantBalance: number | null;
    advertiserStatus: string | null;
    bcId: string | null;
  }> => (await api.get(`${BASE}/accounts/${accountUid}/balance`)).data,

  // Campaigns
  listCampaigns: async (params?: { accountUid?: string }): Promise<TiktokCampaign[]> =>
    (await api.get(`${BASE}/campaigns`, { params })).data,
  createCampaign: async (body: {
    campaignName: string;
    objectiveType: 'TRAFFIC' | 'CONVERSIONS' | 'APP_PROMOTION' | 'REACH' | 'VIDEO_VIEWS' | 'ENGAGEMENT' | 'LEAD_GENERATION' | 'PRODUCT_SALES';
    budgetMode: 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL' | 'BUDGET_MODE_INFINITE';
    budget?: number;
    operationStatus?: 'ENABLE' | 'DISABLE';
  }): Promise<{ campaignId: string }> =>
    (await api.post(`${BASE}/campaigns`, body)).data,

  setCampaignStatus: async (tiktokCampaignId: string, status: 'ENABLE' | 'DISABLE'): Promise<{ ok: true }> =>
    (await api.post(`${BASE}/campaigns/${tiktokCampaignId}/status`, { status })).data,

  // NOTE: never send `billingEvent` — the backend derives it from the matrix
  // (each goal accepts exactly one, so an override can only be wrong).
  createAdGroup: async (body: {
    tiktokCampaignId: string;
    adGroupName: string;
    placements: string[];
    /** Must be a goal the parent campaign's objective allows — see @/lib/tiktok-matrix. */
    optimizationGoal?: string;
    /** Manual bid for goals whose manualBidField is `bid_price` (CPC/CPV/CPM). */
    bidPrice?: number;
    /** Manual bid for OCPM goals — TikTok wants conversion_bid_price there. */
    conversionBidPrice?: number;
    // BID_TYPE_MAX_CONVERSION is rejected by TikTok in every combination
    // ("Invalid CPA smart bid type") — the backend throws on it too.
    bidType?: 'BID_TYPE_NO_BID' | 'BID_TYPE_CUSTOM';
    /** Required for PIXEL_EVENT goals (CONVERSIONS → CONVERT / VALUE). */
    pixelId?: string;
    optimizationEvent?: string;
    /** Required for FREQUENCY_CAP goals (REACH) — show at most N times per M days. */
    frequency?: number;
    frequencySchedule?: number;
    /** APP_PROMOTION only — which registered app to advertise. */
    appId?: string;
    operatingSystems?: string[];
    budgetMode: 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL';
    budget: number;
    scheduleType: 'SCHEDULE_FROM_NOW' | 'SCHEDULE_START_END';
    scheduleStartTime: string;
    scheduleEndTime?: string;
    operationStatus?: 'ENABLE' | 'DISABLE';
    /** Geoname IDs (e.g. ['290557'] for UAE). REQUIRED by TikTok unless zipcodeIds is set. */
    locationIds?: string[];
    zipcodeIds?: string[];
    gender?: 'GENDER_MALE' | 'GENDER_FEMALE' | 'GENDER_UNLIMITED';
    ageGroups?: string[];
    languages?: string[];
  }): Promise<{ adGroupId: string }> =>
    (await api.post(`${BASE}/ad-groups`, body)).data,

  createAd: async (body: {
    tiktokAdGroupId: string;
    adName: string;
    adFormat: 'SINGLE_VIDEO' | 'SINGLE_IMAGE' | 'CAROUSEL_ADS' | 'COLLECTION_ADS';
    adText: string;
    callToAction?: string;
    landingPageUrl?: string;
    videoId?: string;
    imageIds?: string[];
    identityId?: string;
    // CUSTOMIZED_USER | AUTH_CODE | TT_USER | BC_AUTH_TT — pass the type of the
    // selected identity; BC_AUTH_TT is resolved server-side to its BC id.
    identityType?: string;
    identityAuthorizedBcId?: string;
    /** Instant Form id — required for LEAD_GENERATION ads. */
    pageId?: string;
  }): Promise<{ adId: string }> =>
    (await api.post(`${BASE}/ads`, body)).data,

  setAdStatusV2: async (tiktokAdId: string, status: 'ENABLE' | 'DISABLE'): Promise<{ ok: true }> =>
    (await api.post(`${BASE}/ads/${tiktokAdId}/status`, { status })).data,

  syncCampaigns: async (): Promise<{ created: number; updated: number; total: number }> =>
    (await api.post(`${BASE}/campaigns/sync`)).data,

  // Ad Groups
  listAdGroups: async (params?: { campaignId?: string }): Promise<TiktokAdGroup[]> =>
    (await api.get(`${BASE}/ad-groups`, { params })).data,
  syncAdGroups: async (): Promise<{ created: number; updated: number; total: number }> =>
    (await api.post(`${BASE}/ad-groups/sync`)).data,
  setAdGroupStatus: async (tiktokAdGroupId: string, status: 'ENABLE' | 'DISABLE'): Promise<{ ok: true }> =>
    (await api.post(`${BASE}/ad-groups/${tiktokAdGroupId}/status`, { status })).data,

  // Ads
  listAds: async (params?: { adGroupId?: string }): Promise<TiktokAd[]> =>
    (await api.get(`${BASE}/ads`, { params })).data,
  syncAds: async (): Promise<{ created: number; updated: number; total: number }> =>
    (await api.post(`${BASE}/ads/sync`)).data,

  // Reporting
  syncReport: async (body: { daysBack?: number; level?: 'campaign' | 'adgroup' | 'ad'; from?: string; to?: string }): Promise<{ upserted: number; level: string; days: number }> =>
    (await api.post(`${BASE}/reporting/sync`, body)).data,
  aggregateReport: async (params: { level: 'campaign' | 'adgroup' | 'ad'; from?: string; to?: string }): Promise<TiktokInsightsRow[]> =>
    (await api.get(`${BASE}/reporting/aggregate`, { params })).data,
  dailyReport: async (params: {
    level: 'campaign' | 'adgroup' | 'ad';
    entityId: string;
    from?: string;
    to?: string;
  }): Promise<TiktokDailyRow[]> =>
    (await api.get(`${BASE}/reporting/daily`, { params })).data,
  // Account-wide per-day totals — one call for the Overview trend chart.
  dailyTotals: async (params?: {
    level?: 'campaign' | 'adgroup' | 'ad';
    from?: string;
    to?: string;
  }): Promise<TiktokDailyRow[]> =>
    (await api.get(`${BASE}/reporting/daily-totals`, { params })).data,

  // Pixels
  listPixels: async (): Promise<Array<{
    pixelId: string;
    pixelCode: string;
    pixelName?: string;
    events: Array<{ eventType: string; label?: string }>;
  }>> => (await api.get(`${BASE}/pixels`)).data,

  /**
   * Add a web event to a pixel. Required before an ad group can optimise for
   * a conversion — a freshly created pixel has no events.
   * `currency` only accepts USD / JPY / INR on TikTok's side, so it's omitted.
   */
  createPixelEvent: async (pixelId: string, body: { eventType: string; eventName?: string }):
    Promise<{ ok: boolean; pixelId: string; eventType: string }> =>
    (await api.post(`${BASE}/pixels/${pixelId}/events`, body)).data,

  /**
   * Same call as listPixels, normalised for the optimisation-event pickers.
   * TikTok (and older backend builds) return snake_case rows, newer builds
   * return camelCase with an `events` array — accept either, and never throw
   * on a pixel that has no events (that case is surfaced in the UI instead).
   */
  listPixelOptions: async (): Promise<TiktokPixelOption[]> => {
    const raw = (await api.get(`${BASE}/pixels`)).data;
    return normalizePixelOptions(raw);
  },
  createPixel: async (body: { name: string; category?: string }): Promise<{ pixelId: string; pixelCode: string; name: string; category?: string }> =>
    (await api.post(`${BASE}/pixels`, body)).data,
  sendPixelEvent: async (pixelCode: string, body: { event: string; event_id: string; event_time: number; properties?: Record<string, unknown>; user?: Record<string, unknown> }): Promise<{ ok: boolean; message?: string }> =>
    (await api.post(`${BASE}/pixels/${pixelCode}/event`, body)).data,

  // Creatives
  // `imagekitUrl` is the permanent rehosted mirror — prefer it when rendering.
  // TikTok's `imageUrl` is a signed URL that expires in a few hours; backend
  // mirrors on first list fetch.
  listImages: async (params?: { page?: number; pageSize?: number }): Promise<Array<{ imageId: string; imageUrl: string; imagekitUrl?: string; width?: number; height?: number; fileName?: string }>> =>
    (await api.get(`${BASE}/creatives/images`, { params })).data,
  /** Videos already uploaded to the advertiser — pick one instead of re-uploading. */
  listVideos: async (params?: { page?: number; pageSize?: number }): Promise<TiktokLibraryVideo[]> =>
    (await api.get(`${BASE}/creatives/videos`, { params })).data,
  /** Distinct video/image creatives (+ copy) from past synced ads. */
  listCreativesFromAds: async (params?: { limit?: number }): Promise<TiktokAdCreativeRef[]> =>
    (await api.get(`${BASE}/creatives/from-ads`, { params })).data,
  uploadVideoByUrl: async (body: { videoUrl: string; fileName?: string }): Promise<{ videoId: string; videoUrl?: string; status?: string }> =>
    (await api.post(`${BASE}/creatives/videos/by-url`, body)).data,

  videoStatus: async (videoId: string): Promise<{ videoId: string; status?: string; videoUrl?: string; coverUrl?: string; width?: number; height?: number; bitRateKbps?: number; durationSec?: number; displayable?: boolean }> =>
    (await api.get(`${BASE}/creatives/videos/${videoId}/status`)).data,

  uploadImageByUrl: async (body: { imageUrl: string; fileName?: string }): Promise<{ imageId: string; imageUrl: string; width?: number; height?: number; fileName?: string }> =>
    (await api.post(`${BASE}/creatives/images`, body)).data,

  uploadImageByBase64: async (body: { base64: string; fileName?: string; mimeType?: string }): Promise<{ imageId: string; imageUrl: string; width?: number; height?: number; fileName?: string }> =>
    (await api.post(`${BASE}/creatives/images/by-base64`, body)).data,

  uploadImageFile: async (file: File): Promise<{ imageId: string; imageUrl: string; width?: number; height?: number; fileName?: string }> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return (await api.post(`${BASE}/creatives/images/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  uploadVideoFile: async (file: File): Promise<{ videoId: string }> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return (await api.post(`${BASE}/creatives/videos/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  deleteImages: async (imageIds: string[]): Promise<{ deleted: number }> =>
    (await api.delete(`${BASE}/creatives/images`, { data: { imageIds } })).data,

  deleteVideos: async (videoIds: string[]): Promise<{ deleted: number }> =>
    (await api.delete(`${BASE}/creatives/videos`, { data: { videoIds } })).data,

  // Identities
  /** Apps registered on the advertiser — APP_PROMOTION ad groups target one. */
  listApps: async (expectedAccountUid?: string): Promise<Array<{ appId: string; appName: string; packageName?: string; platform: string }>> =>
    (await api.get(`${BASE}/apps`, { params: { expectedAccountUid } })).data,

  /**
   * Instant Forms on the advertiser. LEAD_GENERATION ads must point at one.
   * Read-only — TikTok has no API to create them (Ads Manager → Assets →
   * Instant Forms).
   */
  listLeadForms: async (): Promise<Array<{ pageId: string; title: string; status?: string }>> =>
    (await api.get(`${BASE}/lead-forms`)).data,

  listIdentities: async (): Promise<Array<{ identityId: string; identityType: string; displayName?: string; profileImage?: string }>> =>
    (await api.get(`${BASE}/identities`)).data,

  createIdentity: async (body: { displayName: string; imageId?: string }): Promise<{ identityId: string; identityType: string; displayName?: string }> =>
    (await api.post(`${BASE}/identities`, body)).data,

  // Regions — TikTok-supported geo targets for THIS advertiser. Used to
  // populate the country picker on ad-group create.
  // `objectiveType` is required by TikTok — different objectives unlock
  // different country sets. Defaults to TRAFFIC server-side.
  listRegions: async (params?: { placement?: string; objectiveType?: string }): Promise<Array<{ locationId: string; name: string; regionCode?: string; level?: string }>> =>
    (await api.get(`${BASE}/regions`, { params })).data,

  // Audiences
  listAudiences: async (): Promise<TiktokAudience[]> => (await api.get(`${BASE}/audiences`)).data,
  createAudienceFromGroup: async (body: { groupUid: string; audienceName: string }): Promise<{ audienceId: string; uploaded: number }> =>
    (await api.post(`${BASE}/audiences/from-group`, body)).data,
  createLookalike: async (body: { seedAudienceId: string; name: string; countryCode: string; sizeFactor?: number }): Promise<{ audienceId: string }> =>
    (await api.post(`${BASE}/audiences/lookalike`, body)).data,

  // Event rules — CRM event → TikTok pixel event mapping
  listEventRules: async (): Promise<TiktokEventRule[]> =>
    (await api.get(`${BASE}/event-rules`)).data,
  createEventRule: async (body: TiktokEventRuleInput): Promise<TiktokEventRule> =>
    (await api.post(`${BASE}/event-rules`, body)).data,
  updateEventRule: async (ruleUid: string, body: Partial<TiktokEventRuleInput>): Promise<TiktokEventRule> =>
    (await api.put(`${BASE}/event-rules/${ruleUid}`, body)).data,
  deleteEventRule: async (ruleUid: string): Promise<{ ok: true }> =>
    (await api.delete(`${BASE}/event-rules/${ruleUid}`)).data,
  fireEventRule: async (ruleUid: string, properties?: Record<string, unknown>): Promise<{ ok: boolean; message?: string }> =>
    (await api.post(`${BASE}/event-rules/${ruleUid}/fire`, { properties })).data,

  // Wizard — single-shot launch (campaign + ad group + ad)
  wizardLaunch: async (body: WizardLaunchPayload): Promise<WizardLaunchResult> =>
    (await api.post(`${BASE}/wizard/launch`, body)).data,

  // AI ad-copy enhancement — takes whatever draft fields the user typed,
  // returns clean enhanced copy + recommended CTA. Uses the company's default
  // AI provider/model unless explicitly overridden.
  enhanceAdCopy: async (body: TiktokEnhanceAdCopyPayload): Promise<TiktokEnhancedAdCopy> =>
    (await api.post(`${BASE}/ai/enhance-ad-copy`, body)).data,

  // Workspace bulk activate — campaign + ad groups + ads in one call.
  activateTree: async (body: {
    tiktokCampaignId: string;
    tiktokAdGroupIds: string[];
    tiktokAdIds: string[];
  }): Promise<{
    campaign: { id: string; ok: boolean };
    adGroups: { ok: number; total: number };
    ads: { ok: number; total: number };
  }> => (await api.post(`${BASE}/activate-tree`, body)).data,
};

export type WizardGoal =
  | 'video_views'
  | 'traffic'
  | 'conversions'
  | 'app_installs'
  | 'awareness'
  | 'lead_generation';

export interface WizardLaunchPayload {
  registryAppUid?: string;
  expectedAccountUid?: string;
  appId?: string;
  pageId?: string;
  pixelId?: string;
  optimizationEvent?: string;
  goal: WizardGoal;
  name: string;
  dailyBudget: number;
  startTime?: string;
  endTime?: string;
  videoId: string;
  identityId: string;
  identityType?: string;
  adText: string;
  callToAction?: string;
  landingPageUrl?: string;
  placements?: string[];
  /** Geoname IDs — TikTok requires geo targeting on every ad group. Backend defaults to UAE. */
  locationIds?: string[];
  launchActive?: boolean;
}

export interface WizardLaunchResult {
  campaignId: string;
  adGroupId: string;
  adId: string;
  derived: { objectiveType: string; optimizationGoal: string; billingEvent: string };
}
